import qs from 'qs'
import axios, { AxiosError } from 'axios'
import { schema, normalize, NormalizedSchema } from 'normalizr'
import { TableParams } from '../../models/TableParams'
import { AppDispatch } from '../../reducers/store'
import { APIResponse, MessageReponse } from '../setupAxios'
import { getAdminsSuccess } from '../../reducers/UserReducer'
import { getFilesSuccess } from '../../reducers/FileReducer'
import { getFileTypesSuccess } from '../../reducers/FileTypeReducer'
import { getPermissionsSuccess } from '../../reducers/PermissionReducer'
import { getAddressSuccess } from '../../reducers/AddressReducer'
import { getTenantsSuccess } from '../../reducers/TenantReducer'
import { getBannersSuccess } from '../../reducers/BannerReducer'

export const SBAPIFetchPaginatedDispatch =
  <T>(
    endpoint: string,
    params: TableParams,
    schema: schema.Entity | schema.Entity[],
    actions: any,
    startLoading?: any,
    setIds?: any,
    setPagination?: any
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(startLoading ? startLoading() : actions.startLoading())
    return SBAPIFetchPaginated(endpoint, params)
      .then((data) => {
        const normalizedData = normalize(data.data, schema)
        // Fill store with Normalized data
        dispatch(fillStoreWithNormalizedData(normalizedData))

        // Set store IDs
        dispatch(
          setIds
            ? setIds(normalizedData.result)
            : actions.setIds(normalizedData.result)
        )

        // Set store pagination
        if (setPagination) {
          dispatch(
            setPagination({
              total: data.totalRecords,
              current: data.currentPage,
            })
          )
        } else if (actions.setPagination) {
          dispatch(
            actions.setPagination({
              total: data.totalRecords,
              current: data.currentPage,
            })
          )
        }

        actions.stopLoading ? dispatch(actions.stopLoading()) : {}
      })
      .catch((reason: { message: string | undefined; error: AxiosError }) => {
        actions.hasError ? dispatch(actions.hasError(reason.message)) : {}
        throw reason // Throw error again to allow execution of others catch()
      })
  }
export const SBAPIFetchPaginated = async <T>(
  endpoint: string,
  params: TableParams
) =>
  await axios
    .get(`${endpoint}?${qs.stringify(params)}`)
    .then((response) => {
      const data = response.data
      return data
    })
    .catch((error: AxiosError) => {
      const data = error.response?.data as APIResponse<MessageReponse>
      const errorMessage = data.data?.message ?? ''
      throw { message: errorMessage, error: error } // Throw error again to allow execution of others catch()
    })

export const SBAPIFetchDispatch =
  <T>(
    endpoint: string,
    schema: schema.Entity | schema.Entity[],
    actions: any
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.startLoading())
    return SBAPIFetch(endpoint)
      .then((data) => {
        const normalizedData = normalize(data.data, schema)
        dispatch(fillStoreWithNormalizedData(normalizedData))
        actions.stopLoading ? dispatch(actions.stopLoading()) : {}
      })
      .catch((reason: { message: string | undefined; error: AxiosError }) => {
        actions.hasError ? dispatch(actions.hasError(reason.message)) : {}
        throw reason // Throw error again to allow execution of others catch()
      })
  }

export const SBAPIFetch = async <T>(endpoint: string, params?: any) =>
  await axios
    .get(params ? `${endpoint}?${qs.stringify(params)}` : endpoint)
    .then((response) => {
      const data = response.data
      return data
    })
    .catch((error: AxiosError) => {
      const data = error.response?.data as APIResponse<MessageReponse>
      const errorMessage = data.data?.message ?? ''
      throw { message: errorMessage, error: error } // Throw error again to allow execution of others catch()
    })

export const SBAPICreate =
  <T>(
    model: T,
    endpoint: string,
    schema: schema.Entity | schema.Entity[],
    actions: any
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.startLoading())
    return await axios
      .post(endpoint, model)
      .then((response) => {
        // TODO: Do we really need to process creation result ?
        // As index requests might be triggered after creation...
        const data = response.data
        if (!data.data) {
          // We still need to stop loading
          actions.stopLoading ? dispatch(actions.stopLoading()) : {}
          return
        }
        const normalizedData = normalize(data.data, schema)
        dispatch(fillStoreWithNormalizedData(normalizedData))
        actions.stopLoading ? dispatch(actions.stopLoading()) : {}
      })
      .catch((error: AxiosError) => {
        const data = error.response?.data as APIResponse<MessageReponse>
        actions.hasError
          ? dispatch(actions.hasError(data.data?.message ?? ''))
          : {}

        throw error // Throw error again to allow execution of others catch()
      })
  }

export const SBAPIUpdate =
  <T>(model: T, endpoint: string, actions: any) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.startLoading())

    /**
     * The plan is to immediately dispatch the updated item
     * However in case of error we need to copy a copy to revert the change
     */
    const oldModel = Object.assign({}, model)
    dispatch(actions.updateItemSuccess(model))

    return await axios
      .put(endpoint, model)
      .then((response) => {
        // We do not process the result here, kick back and relax ...
      })
      .catch((error: AxiosError) => {
        const data = error.response?.data as APIResponse<MessageReponse>
        // Revert the model update
        dispatch(actions.updateItemSuccess(oldModel))
        actions.hasError
          ? dispatch(actions.hasError(data.data?.message ?? ''))
          : {}
        throw error // Throw error again to allow execution of others catch()
      })
  }

export const SBAPIDelete =
  <T>(model: T, endpoint: string, actions: any) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.startLoading())
    return await axios
      .delete(endpoint)
      .then(() => {
        dispatch(actions.deleteItemSuccess(model))
      })
      .catch((error: AxiosError) => {
        const data = error.response?.data as APIResponse<MessageReponse>
        actions.hasError
          ? dispatch(actions.hasError(data.data?.message ?? ''))
          : {}
        throw error // Throw error again to allow execution of others catch()
      })
  }

export const SBAPIPost = async (endpoint: string, body: any) =>
  await axios
    .post(endpoint, body)
    .then((response) => {
      const data = response.data
      return data
    })
    .catch((error: AxiosError) => {
      const data = error.response?.data as APIResponse<MessageReponse>
      throw error // Throw error again to allow execution of others catch()
    })

const fillStoreWithNormalizedData =
  (
    normalizedData: NormalizedSchema<
      {
        [key: string]:
          | {
              [key: string]: any
            }
          | undefined
      },
      any
    >
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(getAdminsSuccess(normalizedData.entities.user ?? {}))
    dispatch(getFileTypesSuccess(normalizedData.entities.fileType ?? {}))
    dispatch(getFilesSuccess(normalizedData.entities.file ?? {}))
    dispatch(getPermissionsSuccess(normalizedData.entities.permission ?? {}))
    dispatch(getAddressSuccess(normalizedData.entities.address ?? {}))
    dispatch(getTenantsSuccess(normalizedData.entities.tenant ?? {}))
    dispatch(getBannersSuccess(normalizedData.entities.banner ?? {}))
    // Add other entities here ...
  }
